import type { Language } from '../content';

export type SalesLead = {
  /** Marketing tier id (essential | professional | enterprise) sent to the API as planCode. */
  planId: string;
  /** Human plan label, used only for the mailto fallback body. */
  plan: string;
  name: string;
  labName: string;
  email: string;
  phone: string;
  country: string;
  users: string;
  notes: string;
  language: Language;
  source: 'marketing-website';
};

export type SalesLeadResult = { mode: 'api' } | { mode: 'email'; href: string };

const salesApiUrl = String(import.meta.env.VITE_SALES_API_URL || '').trim();
export const salesEmail = String(
  import.meta.env.VITE_SALES_EMAIL || 'sales@dentallabsystem.work',
).trim();
export const supportEmail = String(
  import.meta.env.VITE_SUPPORT_EMAIL || 'support@dentallabsystem.work',
).trim();

function createEmailHref(lead: SalesLead): string {
  const isArabic = lead.language === 'ar';
  const subject = isArabic
    ? `طلب عرض سعر — ${lead.labName} — ${lead.plan}`
    : `Quote request — ${lead.labName} — ${lead.plan}`;
  const lines = isArabic
    ? [
        `الخطة: ${lead.plan}`,
        `الاسم: ${lead.name}`,
        `المختبر: ${lead.labName}`,
        `البريد: ${lead.email}`,
        `الهاتف: ${lead.phone}`,
        `الدولة: ${lead.country}`,
        `عدد المستخدمين: ${lead.users}`,
        `الأولوية أو الملاحظات: ${lead.notes || '—'}`,
      ]
    : [
        `Plan: ${lead.plan}`,
        `Name: ${lead.name}`,
        `Laboratory: ${lead.labName}`,
        `Email: ${lead.email}`,
        `Phone: ${lead.phone}`,
        `Country: ${lead.country}`,
        `Expected users: ${lead.users}`,
        `Priority / notes: ${lead.notes || '—'}`,
      ];

  return `mailto:${encodeURIComponent(salesEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
}

const planCodePattern = /^[a-z0-9][a-z0-9_]*$/;

/**
 * Maps the form's internal shape to the server's strict SubmitSalesLead schema: field names are
 * renamed (name → contactName, users → expectedUsers), the display label is dropped in favour of
 * the stable plan id, and `source` is never sent — the server stamps it. Any extra key would be
 * rejected by the strict schema, so this is the single source of truth for the request body.
 */
function toApiPayload(lead: SalesLead): Record<string, string> {
  const payload: Record<string, string> = {
    contactName: lead.name,
    labName: lead.labName,
    email: lead.email,
    phone: lead.phone,
    language: lead.language,
  };
  if (planCodePattern.test(lead.planId)) payload.planCode = lead.planId;
  if (lead.country.trim()) payload.country = lead.country;
  if (lead.users.trim()) payload.expectedUsers = lead.users;
  if (lead.notes.trim()) payload.notes = lead.notes;
  return payload;
}

export async function submitSalesLead(lead: SalesLead): Promise<SalesLeadResult> {
  if (!salesApiUrl) {
    return { mode: 'email', href: createEmailHref(lead) };
  }

  const response = await fetch(salesApiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(toApiPayload(lead)),
  });

  if (!response.ok) {
    throw new Error(`Sales request failed with status ${response.status}`);
  }

  return { mode: 'api' };
}
